import useAssetUpload from '@dyl/hooks/useAssetUpload';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useImages } from "@dyl/systems/ImagesManager";
import { useAudios } from "@dyl/systems/AudiosManager";
import { DialogContentText } from "@mui/material";

/** @typedef {'waiting' | 'uploading' | 'uploaded' | 'error'} Status */

/**
 * @typedef FileEntry
 * @property {File} file
 * @property {Status} status
 * @property {() => void} remove
 * @property {(status: Status) => void} setStatus
 */

export default function useLogic(openDialog, type) {
    const { enqueueSnackbar } = useSnackbar();

    const [files, setFiles] = useState(
        /** @returns {FileEntry[]} */
        () => []
    );

    const [isUploading, setUploading] = useState(false);
    const [isDirty, setDirty] = useState(false);

    const addFiles = useCallback(
        /** @param {File[]} files */
        files => {
            setFiles(prev => {
                // Exclude files already in selection
                files = files.filter(a => prev.every(b => a.name !== b.file.name));

                return [
                    ...prev,
                    ...files.map(file => ({
                        file,
                        status: 'waiting',
                        remove() {
                            setFiles(prev => prev.filter(f => f.file !== file));
                        },
                        /** @param {Status} status */
                        setStatus(status) {
                            setFiles(prev => prev.map(f => (
                                f.file === file ? { ...f, status }
                                : f
                            )));
                        },
                    })),
                ];
            });
        }, []
    );

    const clearFiles = useCallback(() => setFiles([]), []);

    const currentUpload = useMemo(() => {
        if (!isUploading) return null;

        return (
            files.find(f => f.status === 'uploading')
            ?? files.find(f => f.status === 'waiting')
        );
    }, [files, isUploading]);

    const { images } = useImages();
    const uploadAssetImage = useAssetUpload(images);
    const { audios } = useAudios();
    const uploadAssetAudio = useAssetUpload(audios, 'ITEM');
    const { audios: motivationalAudios } = useAudios('MOTIVATIONAL');
    const uploadAssetMotivationalAudio = useAssetUpload(motivationalAudios, 'MOTIVATIONAL');

    useEffect(() => {
        if (!currentUpload) {
            setUploading(false);
            return;
        }

        (async () => {
            const { file, setStatus } = currentUpload;

            const ftype = (
                file.type.includes('image') ? 'images'
                : file.type.includes('audio') ? 'sounds'
                : null
            );

            const hasDoubleExtension = (filename) => {
                const parts = filename.split('.');
                return parts.length > 2;
            };

            if (!ftype || hasDoubleExtension(file.name)) {
                const reason = !ftype
                    ? 'has an unrecognized file type'
                    : 'has an invalid file name';
                enqueueSnackbar(`${file.name} ${reason}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                setStatus('error');
                return;
            }

            if (file.size > 2 * 1024 * 1024) {
                enqueueSnackbar(`Upload Failed: The file size exceeds the 2MB limit. Please upload a smaller file to ensure an optimal student experience.`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                setStatus('error');
                return;
            }

            if (file.size > 1024*1024) {
                const confirmed = await new Promise(resolve => openDialog("File larger than 1mb",
                    <>
                        <DialogContentText>
                            WARNING: this file is very large in size and probably shouldn’t be.
                            Please check with a designer to make sure it’s as optimised as possible before upload.
                        </DialogContentText>
                    </>,
                    [
                        { text: 'Upload Anyway', onClick: () => resolve(true) },
                        { text: 'Cancel', onClick: () => resolve(false), destructive: true },
                    ],
                ));
                if (!confirmed) {
                    setStatus('error');
                    return;
                }
            }

            setStatus('uploading');

            try {
                switch (type) {
                    case 'images':
                        await uploadAssetImage(ftype, file);
                        break;
                    case 'item audios':
                        await uploadAssetAudio(ftype, file);
                        break;
                    case 'motivational audios':
                        await uploadAssetMotivationalAudio(ftype, file);
                        break;
                }
            } catch (e) {
                console.error(e);
                enqueueSnackbar(`${e}`, {
                    variant: 'error',
                });
                setStatus('error');
                return;
            }

            setStatus('uploaded');
            setDirty(true);
        })();
    }, [currentUpload?.file]);

    const startUploading = useCallback(() => setUploading(true), []);

    const allowUpload = !isUploading && files.some(f => f.status !== 'uploaded');

    return {
        files, addFiles, clearFiles,
        isUploading,
        startUploading,
        allowUpload,
        isDirty, setDirty,
    };
}